<script setup lang="ts">
/**
 * 代管站「同期限改內容」（批151 裁示 99／109）——站台詳情的動作。
 *
 * 以現行證為底，只改模組／配額／行為參數，**期限、套裝、試用、機器綁定一律沿用**後重簽；
 * 舊證標成「已被取代」，agent 下一輪心跳就把新證寫進站台（不需要另外派工）。
 * 代管站的證不走〈授權〉的換發（那支對代管站一律擋），這裡是期中改內容的專用入口。
 *
 * ## 只送有改的欄
 *
 * 後端三欄都是「給了就整包取代」，所以送出時只帶與開窗基準不同的欄——規則與理由見
 * `lib/siteContent.ts`。三個編輯器（沿用 `ModulePicker`／`QuotaEditor`／`PolicyEditor`）
 * 都是「掛載時 initFrom＋回寫一次」，基準照要在它們回寫**之後**才拍。
 *
 * ## 權限
 *
 * admin＋內容閘（與開站同一套「只看偏離」，`lib/hostedContent.ts`）：重簽後的內容與這張證的
 * 套裝模板一致 → admin 可改；偏離（或這張證沒有套裝）→ 營運主管，否則後端 403 `role_required`
 * ＋`policy_reason`。admin 偏離時這裡先提示並擋住送出，不要按了才 403。
 * operator 根本看不到這顆按鈕（站台詳情頁依角色藏）。
 */
import { computed, nextTick, ref, watch } from 'vue'

import ModulePicker from '@/components/ModulePicker.vue'
import PolicyEditor from '@/components/PolicyEditor.vue'
import QuotaEditor from '@/components/QuotaEditor.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import type { ProductSpec } from '@/api/ls'
import * as sitesApi from '@/api/sites'
import type { SiteLicense, SiteLicenseContentResult } from '@/api/sites'
import { hostedContentBlocked, hostedContentHint, hostedContentReason } from '@/lib/hostedContent'
import { planByKey } from '@/lib/products'
import {
  CONTENT_FIELD_LABELS,
  type ContentField,
  carriedTerm,
  contentErrText,
  contentPatch,
  snapshotContent,
} from '@/lib/siteContent'
import { planLabel } from '@/lib/quotas'
import { seedQuotas } from '@/lib/seedQuotas'
import { formatRemaining } from '@/lib/terms'
import { useAuthStore } from '@/stores/auth'

const props = defineProps<{
  open: boolean
  siteId: string
  siteSlug: string
  license: SiteLicense | null
  spec: ProductSpec | null
}>()
const emit = defineEmits<{ close: []; done: [SiteLicenseContentResult] }>()

const modules = ref<Record<string, string>>({})
const quotas = ref<Record<string, number | null>>({})
const policy = ref<Record<string, unknown>>({})
const quotaValid = ref(true)
const note = ref('')
const editorKey = ref(0)
/** 開窗後三個編輯器回寫完的基準照；`null`＝還在準備（不給送出）。 */
const baseline = ref<Record<ContentField, string> | null>(null)
const busy = ref(false)
const error = ref('')

watch(
  () => props.open,
  async (open) => {
    if (!open || !props.license) return
    const lic = props.license
    baseline.value = null
    error.value = ''
    note.value = ''
    quotaValid.value = true
    modules.value = { ...(lic.modules ?? {}) }
    // 舊版簽的證，數量可能還在一級欄——`seedQuotas` 在鍵缺席時才併進來（明確的不限不會被蓋掉）。
    quotas.value = seedQuotas(props.spec, lic) ?? {}
    policy.value = { ...(lic.policy ?? {}) }
    editorKey.value += 1
    // 第一個 tick：modal 內容掛載、三個編輯器 onMounted 回寫；第二個 tick：回寫落到本元件的 ref。
    await nextTick()
    await nextTick()
    baseline.value = snapshotContent({
      modules: modules.value,
      quotas: quotas.value,
      policy: policy.value,
    })
  },
  { immediate: true },
)

const patch = computed(() =>
  baseline.value
    ? contentPatch(baseline.value, { modules: modules.value, quotas: quotas.value, policy: policy.value })
    : {},
)
const changedLabels = computed(() =>
  (Object.keys(patch.value) as ContentField[]).map((k) => CONTENT_FIELD_LABELS[k]),
)
const hasPolicies = computed(() => (props.spec?.policies?.length ?? 0) > 0)

const auth = useAuthStore()
/**
 * 內容閘的即時判斷——判的是**重簽後的內容**：改了的欄用畫面上的值，沒改的欄＝證上原值
 * （後端 `_carry_over` 沿用的就是它）；期限是「同期限」，已蓋章的與永久證不比、還沒蓋章的
 * 照證上的期限（`lib/siteContent.ts::carriedTerm`，後端 `carry_over_expiry` 的鏡像）；
 * 試用沿用證上的旗標。行為參數不進判準（裁示 107）。
 */
const gateReason = computed(() => {
  const lic = props.license
  if (!lic || !baseline.value) return null
  const p = patch.value
  return hostedContentReason(props.spec, planByKey(props.spec, lic.plan), {
    modules: p.modules ?? lic.modules ?? {},
    quotas: p.quotas ?? lic.quotas ?? {},
    ...carriedTerm(lic),
    trial: lic.trial,
  })
})
/** admin 偏離＝送出必 403 → 先擋（營運主管以上只提示）。 */
const gateBlocked = computed(() => hostedContentBlocked(auth.role, gateReason.value))
const gateHint = computed(() => hostedContentHint(gateReason.value, gateBlocked.value, '改內容'))

const canSubmit = computed(
  () =>
    !!baseline.value &&
    changedLabels.value.length > 0 &&
    quotaValid.value &&
    !gateBlocked.value &&
    !busy.value,
)

function fmt(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '首次取證才起算'
}

async function submit() {
  if (!canSubmit.value) return
  busy.value = true
  error.value = ''
  try {
    const res = await sitesApi.changeSiteLicenseContent(props.siteId, {
      ...patch.value,
      note: note.value.trim() || null,
    })
    emit('done', res)
  } catch (e) {
    error.value = contentErrText(e)
    // 錯誤寫在表單最下面——編輯器很長，不捲過去的話操作者只看到「按了沒反應」。
    await nextTick()
    errorEl.value?.scrollIntoView({ block: 'nearest' })
  } finally {
    busy.value = false
  }
}
const errorEl = ref<HTMLElement | null>(null)
</script>

<template>
  <UiModal
    :open="open"
    :title="`同期限改內容 — ${siteSlug}`"
    width="640px"
    :close-on-backdrop="false"
    @close="emit('close')"
  >
    <template v-if="license">
      <p class="hint">
        以這一站<strong>目前生效的授權</strong>為底重簽一張：只改下面三項，
        <strong>期限、套裝、試用與機器綁定一律沿用</strong>，目前那張會標成「已被取代」，
        agent 下一輪心跳就把新授權寫進站台。要換套裝或延期請走授權申請。
      </p>
      <div class="base">
        <span>套裝 <strong>{{ license.plan_label ?? planLabel(spec, license.plan) }}</strong></span>
        <span>
          到期 {{ fmt(license.expires_at) }}
          <small v-if="formatRemaining(license.expires_at)" class="subtle">（{{ formatRemaining(license.expires_at) }}）</small>
        </span>
        <span v-if="license.trial" class="chip">試用</span>
      </div>

      <div :key="editorKey" class="body">
        <section class="sec">
          <div class="sec-title">模組</div>
          <!-- 批155：內部模組只有營運主管以上能動（後端 403 `internal_plan_requires_manager`）。 -->
          <ModulePicker v-model="modules" :spec="spec" :allow-internal="auth.isManagerOrAbove" />
        </section>
        <section class="sec">
          <div class="sec-title">配額</div>
          <QuotaEditor v-model="quotas" :spec="spec" @update:valid="quotaValid = $event" />
        </section>
        <section v-if="hasPolicies" class="sec">
          <div class="sec-title">行為參數</div>
          <PolicyEditor v-model="policy" :spec="spec" />
        </section>
        <label class="note">
          <span>備註（選填，只進稽核）</span>
          <input v-model="note" maxlength="500" placeholder="例：客戶加購後調整配額" />
        </label>
      </div>

      <p class="gate">管理員可以改成與套裝模板一致的內容；偏離套裝模板（或這張授權沒有套裝）需「營運主管」以上。</p>
      <p v-if="gateHint" :class="gateBlocked ? 'warn-text' : 'subtle small'">
        {{ gateBlocked ? '⚠️ ' : 'ⓘ ' }}{{ gateHint }}
      </p>
      <p v-if="!quotaValid" class="warn-text">配額還有必填的項目沒填，填完才能送出。</p>
      <p v-if="baseline && !changedLabels.length" class="subtle small">尚未改動任何項目。</p>
      <p v-else-if="changedLabels.length" class="subtle small">將重簽並更新：{{ changedLabels.join('、') }}（沒改的項目不會送出）。</p>
      <p v-if="error" ref="errorEl" class="warn-text">{{ error }}</p>
    </template>

    <template #foot>
      <!-- 表單很長，閘的完整說明在最下面——按鈕旁再講一次「為什麼是灰的」。 -->
      <span v-if="gateBlocked" class="foot-note">偏離套裝，需營運主管</span>
      <UiButton @click="emit('close')">取消</UiButton>
      <UiButton variant="primary" :loading="busy" :disabled="!canSubmit" @click="submit">重簽授權</UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.hint {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-13);
  color: var(--fg-2);
  line-height: var(--lh-base);
}
.base {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.body {
  display: grid;
  gap: var(--sp-3);
  margin-top: var(--sp-3);
}
.sec-title {
  margin-bottom: 4px;
  font-size: var(--fs-12);
  font-weight: 600;
  color: var(--fg-2);
}
.note {
  display: grid;
  gap: 4px;
}
.note span {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.note input {
  width: 100%;
  min-width: 0;
}
.gate {
  margin: var(--sp-3) 0 0;
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.small {
  margin: 6px 0 0;
  font-size: var(--fs-12);
}
.warn-text {
  margin: var(--sp-2) 0 0;
  color: var(--warning);
  font-size: var(--fs-12);
}
.foot-note {
  align-self: center;
  margin-right: auto;
  color: var(--warning);
  font-size: var(--fs-12);
}
</style>
